import React, { useEffect, useState } from "react";
import TableCustom from "../../../../components/table/tableCustom";
import TableRowCommon from "../../../../components/table/TableRowCommon";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { dateTimeToView } from "../../../../utils/date.utils";
import { routeName } from "../../../../routes/routeName";
import { icons } from "../../../../assets/icons/icons";
import CustomStatus from "../../../../components/chip/CustomStatus";
import { FiberManualRecord } from "@mui/icons-material";
import CheckBoxCustom from "../../../../components/checkbox/CheckBoxCustom";

interface ListAdvisoryRequestProps {
  data: any;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  totalItems: number;
  sortBy: string;
  sortType: string;
  pageLimit: number;
  setPageLimit: React.Dispatch<React.SetStateAction<number>>;
  selectedDataApprove: any[];
  setSelectedDataApprove: React.Dispatch<React.SetStateAction<any>>;
}

const ListAdvisoryRequest: React.FC<ListAdvisoryRequestProps> = ({
  data,
  page,
  setPage,
  totalItems,
  sortBy,
  sortType,
  pageLimit,
  setPageLimit,
  selectedDataApprove,
  setSelectedDataApprove,
}) => {
  const navigate: NavigateFunction = useNavigate();

  const [isCheckAll, setIsCheckAll] = useState(false);

  const handleCheckAll = (isChecked: boolean, data: any[]) => {
    if (isChecked) {
      // Filter data for "รอพิจารณา" status and collect their ids
      const allPendingIds = data
        .filter((item) => item?.status_request === "รอพิจารณา")
        .map((item) => item?.id_advisor_request);

      setSelectedDataApprove(allPendingIds);
    } else {
      setSelectedDataApprove([]);
    }
    setIsCheckAll(isChecked);
  };

  useEffect(() => {
    if (isCheckAll) {
      // status_request === "รอพิจารณา"
      handleCheckAll(true, data);
    } else {
      handleCheckAll(false, []);
      setSelectedDataApprove([]);
    }
  }, [isCheckAll]);

  const handleCheckboxChange = (id: number, isChecked: boolean) => {
    setSelectedDataApprove(
      (prevSelected: any) =>
        isChecked
          ? [...prevSelected, id] // Add the id if checked
          : prevSelected.filter((item: any) => item !== id) // Remove the id if unchecked
    );
  };

  const headCells = [
    {
      id: "checkbox",
      width: 2,
      disablePadding: true,
      align: "start",
      label: (
        <CheckBoxCustom
          checked={isCheckAll}
          setChecked={
            (status: boolean) => handleCheckAll(status, selectedDataApprove) // Handle check all
          }
        />
      ),
    },
    {
      id: "fullname",
      width: 50,
      disablePadding: true,
      align: "start",
      label: "ชื่อ - นามสกุล",
    },
    {
      id: "nickname",
      disablePadding: true,
      width: 15,
      align: "start",
      label: "ฉายา",
    },
    {
      id: "student_no",
      disablePadding: false,
      align: "center",
      width: 15,
      label: "รหัสประจำตัวนักศึกษา",
    },
    {
      id: "degree",
      disablePadding: false,
      align: "center",
      width: 15,
      label: "ระดับ",
    },
    {
      id: "course",
      disablePadding: false,
      align: "center",
      width: 15,
      label: "หลักสูตร",
    },
    {
      id: "year",
      disablePadding: false,
      align: "center",
      width: 50,
      label: "ชั้นปีที่",
    },
    {
      id: "date_request",
      disablePadding: false,
      align: "center",
      width: 20,
      label: "วันที่ส่งคำขอ",
    },
    {
      id: "status",
      disablePadding: false,
      align: "center",
      width: 20,
      label: "สถานะ",
    },
    {
      id: "action",
      disablePadding: false,
      align: "center",
      width: 20,
      label: "action",
    },
  ];

  const handleStatus = (status: string) => {
    if (status === "รอพิจารณา") {
      return "warning";
    } else if (status === "ตอบรับเป็นที่ปรึกษา") {
      return "success";
    } else if (status === "ปฏิเสธคำขอ") {
      return "error";
    } else if (status === "รออาจารย์ที่ปรึกษาพิจารณาคำร้อง") {
      return "warning";
    } else if (status === "รออาจารย์ที่ปรึกษาใหม่พิจารณาคำร้อง") {
      return "warning";
    } else if (status === "สิ้นสุดการเป็นอาจารย์ที่ปรึกษา") {
      return "error";
    } else {
      return "default";
    }
  };

  const renderData = (objData: any, no: any) => {
    no = page * pageLimit - pageLimit + no + 1;
    const {
      id_advisor_request,
      student_frist_name,
      student_last_name,
      student_no,
      dg_degree_name,
      grade_level,
      faculty_name,
      year,
      created_date,
      student_nickname,
      status_request,
      education_type_name,
    } = objData;

    const isChecked = selectedDataApprove.includes(id_advisor_request);

    const objRenderData = {
      key: id_advisor_request,
      id: no,
      obj: objData,
      columns: [
        {
          option: "TEXT",
          align: "start",
          label: (
            <>
              {status_request === "รอพิจารณา" ? (
                <CheckBoxCustom
                  checked={isChecked}
                  setChecked={(status: boolean) =>
                    handleCheckboxChange(id_advisor_request, status)
                  }
                />
              ) : null}
            </>
          ),
        },
        {
          option: "TEXT",
          align: "start",
          label: student_frist_name + " " + student_last_name,
        },
        {
          option: "TEXT",
          align: "start",
          label: student_nickname,
        },
        {
          option: "TEXT",
          align: "center",
          label: student_no,
        },
        {
          option: "TEXT",
          align: "center",
          disablePadding: true,
          label: dg_degree_name ?? "",
        },
        {
          option: "TEXT",
          align: "center",
          disablePadding: true,
          label: faculty_name,
        },
        {
          option: "TEXT",
          align: "center",
          disablePadding: true,
          label: grade_level,
        },
        {
          option: "TEXT",
          align: "center",
          disablePadding: true,
          label: created_date ? dateTimeToView(created_date) : "-",
        },
        {
          option: "TEXT",
          align: "center",
          // label: status_request,
          label: (
            <CustomStatus
              label={status_request}
              type="outlined"
              color={handleStatus(status_request)}
              icon={<FiberManualRecord />}
              width="250px"
            />
          ),
        },
        {
          option: "TEXT",
          align: "center",
          label: (
            <div
              style={{
                display: "flex",
                textAlign: "center",
                justifyContent: "center",
              }}
            >
              <img
                onClick={() => {
                  navigate(
                    routeName.advisor.manageAdvisorRequest +
                      `/${id_advisor_request}`
                  );
                }}
                src={icons.eye}
                alt="eye"
                style={{ cursor: "pointer" }}
              />
            </div>
          ),
        },
      ],
    };
    return <TableRowCommon {...objRenderData} />;
  };

  return (
    <section className="w-full mt-10">
      <TableCustom
        tableMinWidth={2000}
        tableFixedWidth
        customScroll
        page={page}
        pageLimit={pageLimit}
        sortBy={sortBy}
        sortType={sortType}
        headCells={headCells}
        rowCount={totalItems}
        rowsData={
          data &&
          data.length > 0 &&
          data.map((data: any, i: any) => {
            return renderData(data, i);
          })
        }
        onSort={() => {}}
        setPageLimit={(value: number) => setPageLimit(value)}
        setPage={(value: number) => setPage(value)}
      />
    </section>
  );
};

export default ListAdvisoryRequest;
